import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  makeStyles,
} from "@material-ui/core";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import TabsStepper, { TStepTitle } from "../Tabs/TabsStepper";

import { isEmpty } from "@firebase/util";
import { lang } from "../../../i18nLang";

const useStyles = makeStyles(() => ({
  paper: { width: "640px", height: "544px", maxWidth: "100%" },
  paperLarge: { width: "960px", height: "544px" },
}));

interface IProps extends DialogProps {
  /** Propriedade que indica se o modal está aberto ou fechado. */
  open: boolean;
  /** Título da janela modal. */
  title: string;
  /** Número da passo atual */
  activeStep: number;
  /** Lista de título dos passos a serem criados. Aqui também é possível indicar se os
   * passos estão válidos ou não.
   */
  steps: TStepTitle[];
  /** Label do botão 'Finalizar'. */
  finishedLabel?: string;
  /** Label do botão 'Próximo'. */
  nextLabel?: string;
  /** Label do botão 'Cancelar'. */
  cancelLabel?: string;
  /** Desabilita o botão de próximo/finalizar. */
  disabledFinishButton?: boolean;
  /** Habilita para o modal ser fechado ao clicar fora da janela modal. */
  enableBackdropClickClose?: boolean;
  /** Exibe o controle das etapas e os botões de ação do modal.  */
  showStepsAndActions?: boolean;
  /** Classe a ser aplicada ao componente DialogContent. */
  classNameContent?: string;
  /** Função que será chamada ao fechar o modal. */
  onClose: (event: {}, reason: "backdropClick" | "escapeKeyDown") => void;
  /** Função que será chamada ao clicar no botão 'Finalizar'. */
  onFinish: () => any;
  /** Função que será chamada ao clicar no botão 'Next'. */
  onNext: (currentStep: number, nextStep: number) => void;
  /** Componente com o conteúdo da janela modal. Deve ter os componenetes TabPanel para fazer a troca de abas. */
  children: any;
}

/** Cria um janela modal com passos a serem avançados.
 * @category Components
 */
const DialogModalWithSteps = ({
  open,
  title,
  activeStep,
  steps,
  finishedLabel,
  nextLabel,
  cancelLabel,
  disabledFinishButton,
  enableBackdropClickClose,
  showStepsAndActions,
  onClose,
  onFinish,
  onNext,
  children,
  ...props
}: IProps) => {
  const [currentStep, setCurrentStep] = useState(activeStep);
  const classes = useStyles();

  useEffect(() => {
    if (activeStep !== currentStep) setCurrentStep(activeStep);
  }, [activeStep]);

  const handleClickClose = (ev, reason) => {
    if (enableBackdropClickClose || reason === "click") {
      onClose(ev, reason);
    }
  };

  const handleClickConfirm = async (ev) => {
    if (onFinish) {
      const error = await onFinish();
      if (error) return;
    }
    handleClickClose(ev, "click");
  };

  const handleClickNext = () => {
    const nextStep = currentStep + 1;
    if (isEmpty(steps[nextStep])) return;

    onNext(currentStep, nextStep);
  };

  const handleClickStep = (step: number) => {
    if (step >= currentStep) return;

    onNext(currentStep, step);
  };

  const currentStepIsValid = () => {
    const step = steps[activeStep];
    if (!step) return false;
    if (step.valid === undefined) return true;
    return step.valid;
  };

  const allStepsAreValid = () => {
    return steps.every((step) => step.valid);
  };

  const finished = currentStep === steps.length - 1;
  return (
    <Dialog
      {...props}
      open={open}
      onClose={handleClickClose}
      classes={{
        paper: props.maxWidth === "sm" ? classes.paper : classes.paperLarge,
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      {showStepsAndActions && (
        <TabsStepper
          steps={steps.map((step, index) => ({
            ...step,
            complete: index < currentStep,
          }))}
          activeStep={currentStep}
          stepsClickable={true}
          onClickStep={handleClickStep}
        />
      )}
      <DialogContent className={props.classNameContent}>
        {children}
      </DialogContent>
      {showStepsAndActions && (
        <DialogActions>
          <Button
            id="btnCancelModal"
            color="default"
            onClick={(ev) => handleClickClose(ev, "click")}
          >
            {cancelLabel}
          </Button>

          {!finished && (
            <Button
              id="btnNextModal"
              color="primary"
              className="next-button"
              disabled={!currentStepIsValid()}
              onClick={handleClickNext}
            >
              {nextLabel}
            </Button>
          )}
          {finished && (
            <Button
              id="btnFinishModal"
              color="primary"
              className="finish-button"
              disabled={!allStepsAreValid()}
              onClick={handleClickConfirm}
            >
              {finishedLabel}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

DialogModalWithSteps.propTypes = {
  title: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  activeStep: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onFinish: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  cancelLabel: PropTypes.string,
  finishedLabel: PropTypes.string,
  nextLabel: PropTypes.string,
  disabledConfirm: PropTypes.bool,
  enableBackdropClickClose: PropTypes.bool,
  showStepsAndActions: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

DialogModalWithSteps.defaultProps = {
  cancelLabel: lang("cancel"),
  finishedLabel: lang("finish"),
  nextLabel: lang("continue"),
  disabledFinishButton: false,
  enableBackdropClickClose: false,
  showStepsAndActions: true,
  steps: [],
};

export default DialogModalWithSteps;
